/**
 * Money ⇄ price conversion for SL/TP (proposal 18).
 *
 * Converts an account-currency risk/target amount into a price level using the
 * per-symbol tick values the gateway already streams (tickValue/bidTickValue/
 * askTickValue), and back. Pure functions — no store access.
 *
 * Conventions:
 * - `money` is always a positive amount in account currency ("risk 50 USD").
 * - `side: 'sl'` places the level on the losing side of `basePrice`,
 *   `side: 'tp'` on the winning side, relative to `direction` (buy/sell).
 * - Result is snapped to the symbol tick size.
 */

export interface SlTpSymbolInfo {
  digits: number;
  tickSize: number;
  tickValue: number;
  bidTickValue: number;
  askTickValue: number;
}

export type SlTpSide = "sl" | "tp";
export type SlTpDirection = "buy" | "sell";

/** Effective tick size; falls back to 10^-digits when the feed sends 0. */
export function effectiveTickSize(symbol: SlTpSymbolInfo): number {
  return symbol.tickSize > 0 ? symbol.tickSize : Math.pow(10, -symbol.digits);
}

/** Effective per-lot tick value for a side; falls back to tickValue. */
export function effectiveTickValue(
  symbol: SlTpSymbolInfo,
  side: SlTpSide
): number {
  const sided = side === "sl" ? symbol.bidTickValue : symbol.askTickValue;
  return sided > 0 ? sided : symbol.tickValue;
}

/** Snap a raw price to the symbol tick grid (round to nearest tick). */
export function snapToTick(price: number, symbol: SlTpSymbolInfo): number {
  const tick = effectiveTickSize(symbol);
  const snapped = Math.round(price / tick) * tick;
  // Kill float noise at the symbol's precision.
  return Number(snapped.toFixed(Math.max(symbol.digits, 0)));
}

/**
 * Price level that corresponds to risking/targeting `money` (account currency)
 * at `volume` lots from `basePrice`. Returns null when inputs cannot produce a
 * finite positive price (no tick value yet, zero volume, …).
 */
export function slTpPriceFromMoney(
  money: number,
  side: SlTpSide,
  direction: SlTpDirection,
  basePrice: number,
  volume: number,
  symbol: SlTpSymbolInfo
): number | null {
  if (!(money > 0) || !(volume > 0) || !(basePrice > 0)) return null;
  const tickValue = effectiveTickValue(symbol, side);
  if (!(tickValue > 0)) return null;

  const ticks = money / (tickValue * volume);
  if (!Number.isFinite(ticks)) return null;

  const tick = effectiveTickSize(symbol);
  // buy: SL below, TP above. sell: SL above, TP below.
  const sign =
    (side === "sl" ? -1 : 1) * (direction === "buy" ? 1 : -1);
  const price = basePrice + sign * ticks * tick;
  if (!(price > 0)) return null;
  return snapToTick(price, symbol);
}

/**
 * Inverse: account-currency money represented by the distance between
 * `basePrice` and `price` at `volume` lots. Always returns a positive
 * magnitude (or null when not computable).
 */
export function slTpMoneyFromPrice(
  price: number,
  side: SlTpSide,
  basePrice: number,
  volume: number,
  symbol: SlTpSymbolInfo
): number | null {
  if (!(price > 0) || !(volume > 0) || !(basePrice > 0)) return null;
  const tickValue = effectiveTickValue(symbol, side);
  if (!(tickValue > 0)) return null;

  const tick = effectiveTickSize(symbol);
  const ticks = Math.abs(price - basePrice) / tick;
  const money = ticks * tickValue * volume;
  if (!Number.isFinite(money)) return null;
  return Number(money.toFixed(2));
}
